import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import EmptyState from '@/components/base/EmptyState';
import useSeo from '@/hooks/useSeo';
import { searchArticles, getHotKeywords, getHotArticles } from '@/utils/data';

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get('q') || '';
  const [input, setInput] = useState(keyword);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setInput(keyword);
    setVisible(PAGE_SIZE);
  }, [keyword]);

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 - 色哟哟` : '搜索娱乐资讯 - 色哟哟',
    description: `在色哟哟搜索“${keyword}”相关的娱乐资讯、影视音乐与潮流内容。`,
    keywords: `色哟哟,搜索,${keyword}`,
  });

  const results = useMemo(() => searchArticles(keyword), [keyword]);
  const hot = useMemo(() => getHotArticles(5), []);
  const shown = results.slice(0, visible);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setParams({ q });
  };

  const goKeyword = (k: string) => {
    setInput(k);
    setParams({ q: k });
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100 px-4 py-6 md:px-8 md:py-8">
        <div className="mb-4">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
        </div>
        <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">搜索娱乐资讯</h1>
        <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl items-center gap-2">
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如：暑期档、新专辑、红毯"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-800 outline-none focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">热门关键词：</span>
          {getHotKeywords().map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => goKeyword(k)}
              className="rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700 cursor-pointer"
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        {keyword ? (
          <>
            <p className="mb-5 text-sm text-foreground-500">
              搜索关键词 “<span className="font-semibold text-primary-600">{keyword}</span>”，
              共找到 <span className="font-semibold text-primary-600">{results.length}</span> 条结果
            </p>
            {results.length === 0 ? (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到相关资讯"
                desc="换个关键词试试，或者看看下面这些热门内容。"
                actionLabel="返回首页"
                linkTo="/"
              />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                  {shown.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
                {visible < results.length && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                      加载更多（还剩 {results.length - visible} 条）
                    </button>
                  </div>
                )}
              </>
            )}

            {results.length === 0 && (
              <div className="mt-10">
                <h2 className="mb-4 text-lg font-semibold text-foreground-900">热门推荐</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                  {hot.slice(0, 3).map((a) => (
                    <ArticleCard key={a.id} article={a} showSummary={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-8">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              desc="试试搜索“暑期档”“新专辑”“红毯”等关键词，发现更多精彩娱乐内容。"
            />
            <div>
              <h2 className="mb-4 text-lg font-semibold text-foreground-900">大家都在看</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
                {hot.slice(0, 3).map((a) => (
                  <ArticleCard key={a.id} article={a} showSummary={false} />
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground-500 transition-colors hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}